/**
 * AnnouncementCreator Component
 * Interface for administrators to create and send targeted announcements
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Button,
  Alert,
  Badge,
  Spinner,
  Row,
  Col,
  ButtonGroup,
  Table,
  Tabs,
  Tab,
  ProgressBar,
  Modal
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBullhorn,
  faPaperPlane,
  faCalendar,
  faExclamationCircle,
  faUsers,
  faCheckCircle,
  faHistory,
  faChartBar,
  faEye,
  faClock,
  faCheck,
  faEnvelope
} from '@fortawesome/free-solid-svg-icons';
import { createAnnouncement, getAnnouncementHistory, getAnnouncementStats } from '../../services/messagingService';
import './AnnouncementCreator.css';

/**
 * AnnouncementCreator - Create and send announcements
 * @param {Object} props - Component props
 * @param {Object} props.user - Current admin user
 * @returns {JSX.Element} AnnouncementCreator component
 */
const AnnouncementCreator = ({ user }) => {
  const [activeTab, setActiveTab] = useState('create');
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    targetAudience: 'all',
    priority: 'normal',
    scheduleType: 'immediate',
    scheduledDate: '',
    scheduledTime: ''
  });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  /**
   * Load announcement history when history tab is active
   */
  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory();
    }
  }, [activeTab]);

  /**
   * Load announcement history
   */
  const loadHistory = async () => {
    try {
      setLoadingHistory(true);
      const data = await getAnnouncementHistory();
      setHistory(data || []);
    } catch (err) {
      console.error('Error loading announcement history:', err);
      setError('Failed to load announcement history');
    } finally {
      setLoadingHistory(false);
    }
  };

  /**
   * Handle form field changes
   */
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  /**
   * Validate form data
   */
  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length > 100) {
      newErrors.title = 'Title must be 100 characters or less';
    }

    if (!formData.content.trim()) {
      newErrors.content = 'Content is required';
    } else if (formData.content.length > 2000) {
      newErrors.content = 'Content must be 2000 characters or less';
    }

    if (formData.scheduleType === 'scheduled') {
      if (!formData.scheduledDate) {
        newErrors.scheduledDate = 'Scheduled date is required';
      }
      if (!formData.scheduledTime) {
        newErrors.scheduledTime = 'Scheduled time is required';
      }

      // Validate that scheduled time is in the future
      if (formData.scheduledDate && formData.scheduledTime) {
        const scheduledDateTime = new Date(`${formData.scheduledDate}T${formData.scheduledTime}`);
        if (scheduledDateTime <= new Date()) {
          newErrors.scheduledDate = 'Scheduled time must be in the future';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSending(true);
      setError(null);
      setSuccess(false);

      // Prepare announcement data
      const announcementData = {
        title: formData.title.trim(),
        content: formData.content.trim(),
        targetAudience: formData.targetAudience,
        priority: formData.priority,
        scheduleType: formData.scheduleType
      };

      if (formData.scheduleType === 'scheduled') {
        announcementData.scheduledAt = new Date(
          `${formData.scheduledDate}T${formData.scheduledTime}`
        ).toISOString();
      }

      // Call API to create announcement
      await createAnnouncement(announcementData);

      setSuccess(true);
      
      // Reset form
      setFormData({
        title: '',
        content: '',
        targetAudience: 'all',
        priority: 'normal',
        scheduleType: 'immediate',
        scheduledDate: '',
        scheduledTime: ''
      });

      // Reload history if on history tab
      if (activeTab === 'history') {
        loadHistory();
      }

      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      console.error('Error creating announcement:', err);
      setError(err.message || 'Failed to create announcement');
    } finally {
      setSending(false);
    }
  };

  /**
   * Get audience description
   */
  const getAudienceDescription = (audience) => {
    const descriptions = {
      all: 'All users in the system',
      speakers: 'All speakers',
      attendees: 'All attendees',
      admins: 'All administrators'
    };
    return descriptions[audience] || '';
  };

  /**
   * Get character count color
   */
  const getCharCountColor = (current, max) => {
    const percentage = (current / max) * 100;
    if (percentage >= 90) return 'text-danger';
    if (percentage >= 75) return 'text-warning';
    return 'text-muted';
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Get status badge variant
   */
  const getStatusBadge = (status) => {
    const variants = {
      sent: 'success',
      scheduled: 'info',
      sending: 'warning',
      failed: 'danger'
    };
    return variants[status] || 'secondary';
  };

  /**
   * View announcement details
   */
  const viewAnnouncementDetails = (announcement) => {
    setSelectedAnnouncement(announcement);
    setShowDetailsModal(true);
  };

  /**
   * Render announcement history table
   */
  const renderHistoryTable = () => {
    if (loadingHistory) {
      return (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Loading announcement history...</p>
        </div>
      );
    }

    if (history.length === 0) {
      return (
        <div className="text-center py-5">
          <FontAwesomeIcon icon={faBullhorn} size="3x" className="text-muted mb-3" />
          <p className="text-muted">No announcements sent yet</p>
        </div>
      );
    }

    return (
      <Table responsive hover>
        <thead>
          <tr>
            <th>Title</th>
            <th>Audience</th>
            <th>Priority</th>
            <th>Status</th>
            <th>Sent/Scheduled</th>
            <th>Delivery</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {history.map((announcement) => (
            <tr key={announcement.announcementId}>
              <td>
                <strong>{announcement.title}</strong>
              </td>
              <td>
                <FontAwesomeIcon icon={faUsers} className="me-1" />
                {announcement.targetAudience}
              </td>
              <td>
                <Badge bg={
                  announcement.priority === 'urgent' ? 'danger' :
                  announcement.priority === 'high' ? 'warning' : 'secondary'
                }>
                  {announcement.priority}
                </Badge>
              </td>
              <td>
                <Badge bg={getStatusBadge(announcement.status)}>
                  {announcement.status}
                </Badge>
              </td>
              <td>
                <small className="text-muted">
                  {formatDate(announcement.createdAt)}
                </small>
              </td>
              <td>
                {announcement.deliveryStats && (
                  <div className="delivery-stats">
                    <small>
                      <FontAwesomeIcon icon={faEnvelope} className="me-1" />
                      {announcement.deliveryStats.delivered || 0}/{announcement.deliveryStats.total || 0}
                    </small>
                    {announcement.deliveryStats.read > 0 && (
                      <small className="ms-2">
                        <FontAwesomeIcon icon={faEye} className="me-1" />
                        {announcement.deliveryStats.read}
                      </small>
                    )}
                  </div>
                )}
              </td>
              <td>
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={() => viewAnnouncementDetails(announcement)}
                >
                  <FontAwesomeIcon icon={faEye} className="me-1" />
                  View
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  /**
   * Render announcement details modal
   */
  const renderDetailsModal = () => {
    if (!selectedAnnouncement) return null;

    const stats = selectedAnnouncement.deliveryStats || {};
    const deliveryRate = stats.total > 0 ? (stats.delivered / stats.total * 100).toFixed(1) : 0;
    const readRate = stats.delivered > 0 ? (stats.read / stats.delivered * 100).toFixed(1) : 0;

    return (
      <Modal
        show={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faBullhorn} className="me-2" />
            Announcement Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="announcement-details">
            {/* Title and Priority */}
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-start">
                <h5>{selectedAnnouncement.title}</h5>
                <Badge bg={
                  selectedAnnouncement.priority === 'urgent' ? 'danger' :
                  selectedAnnouncement.priority === 'high' ? 'warning' : 'secondary'
                }>
                  {selectedAnnouncement.priority}
                </Badge>
              </div>
            </div>

            {/* Content */}
            <div className="mb-3">
              <h6>Message</h6>
              <p className="text-muted">{selectedAnnouncement.content}</p>
            </div>

            {/* Metadata */}
            <Row className="mb-3">
              <Col md={6}>
                <h6>Target Audience</h6>
                <p>
                  <FontAwesomeIcon icon={faUsers} className="me-2" />
                  {selectedAnnouncement.targetAudience}
                </p>
              </Col>
              <Col md={6}>
                <h6>Status</h6>
                <p>
                  <Badge bg={getStatusBadge(selectedAnnouncement.status)}>
                    {selectedAnnouncement.status}
                  </Badge>
                </p>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <h6>Created</h6>
                <p className="text-muted">
                  <FontAwesomeIcon icon={faClock} className="me-2" />
                  {formatDate(selectedAnnouncement.createdAt)}
                </p>
              </Col>
              {selectedAnnouncement.scheduledAt && (
                <Col md={6}>
                  <h6>Scheduled For</h6>
                  <p className="text-muted">
                    <FontAwesomeIcon icon={faCalendar} className="me-2" />
                    {formatDate(selectedAnnouncement.scheduledAt)}
                  </p>
                </Col>
              )}
            </Row>

            {/* Delivery Statistics */}
            {stats.total > 0 && (
              <div className="delivery-analytics">
                <h6>Delivery Analytics</h6>
                
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-1">
                    <span>Delivery Rate</span>
                    <span className="fw-bold">{deliveryRate}%</span>
                  </div>
                  <ProgressBar
                    now={deliveryRate}
                    variant={deliveryRate >= 90 ? 'success' : deliveryRate >= 70 ? 'warning' : 'danger'}
                  />
                  <small className="text-muted">
                    {stats.delivered || 0} of {stats.total} delivered
                  </small>
                </div>

                {stats.delivered > 0 && (
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <span>Read Rate</span>
                      <span className="fw-bold">{readRate}%</span>
                    </div>
                    <ProgressBar
                      now={readRate}
                      variant={readRate >= 50 ? 'success' : readRate >= 25 ? 'warning' : 'info'}
                    />
                    <small className="text-muted">
                      {stats.read || 0} of {stats.delivered} read
                    </small>
                  </div>
                )}

                <Row className="text-center mt-3">
                  <Col>
                    <div className="stat-box">
                      <FontAwesomeIcon icon={faUsers} size="2x" className="text-primary mb-2" />
                      <h4>{stats.total || 0}</h4>
                      <small className="text-muted">Total Recipients</small>
                    </div>
                  </Col>
                  <Col>
                    <div className="stat-box">
                      <FontAwesomeIcon icon={faCheck} size="2x" className="text-success mb-2" />
                      <h4>{stats.delivered || 0}</h4>
                      <small className="text-muted">Delivered</small>
                    </div>
                  </Col>
                  <Col>
                    <div className="stat-box">
                      <FontAwesomeIcon icon={faEye} size="2x" className="text-info mb-2" />
                      <h4>{stats.read || 0}</h4>
                      <small className="text-muted">Read</small>
                    </div>
                  </Col>
                </Row>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  return (
    <div className="announcement-creator">
      <div className="creator-header">
        <h3>
          <FontAwesomeIcon icon={faBullhorn} className="me-2" />
          Announcement Management
        </h3>
        <p className="text-muted">
          Create, schedule, and track announcements to users
        </p>
      </div>

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(false)}>
          <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
          Announcement {formData.scheduleType === 'scheduled' ? 'scheduled' : 'sent'} successfully!
        </Alert>
      )}

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <FontAwesomeIcon icon={faExclamationCircle} className="me-2" />
          {error}
        </Alert>
      )}

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-3"
      >
        <Tab
          eventKey="create"
          title={
            <>
              <FontAwesomeIcon icon={faBullhorn} className="me-2" />
              Create Announcement
            </>
          }
        >
          <Card className="creator-card">
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                {/* Title */}
                <Form.Group className="mb-3">
                  <Form.Label>
                    Announcement Title <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter announcement title..."
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    isInvalid={!!errors.title}
                    maxLength={100}
                  />
                  <div className="d-flex justify-content-between">
                    <Form.Control.Feedback type="invalid">
                      {errors.title}
                    </Form.Control.Feedback>
                    <Form.Text className={getCharCountColor(formData.title.length, 100)}>
                      {formData.title.length}/100
                    </Form.Text>
                  </div>
                </Form.Group>

                {/* Content */}
                <Form.Group className="mb-3">
                  <Form.Label>
                    Message Content <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={6}
                    placeholder="Enter announcement message..."
                    value={formData.content}
                    onChange={(e) => handleChange('content', e.target.value)}
                    isInvalid={!!errors.content}
                    maxLength={2000}
                  />
                  <div className="d-flex justify-content-between">
                    <Form.Control.Feedback type="invalid">
                      {errors.content}
                    </Form.Control.Feedback>
                    <Form.Text className={getCharCountColor(formData.content.length, 2000)}>
                      {formData.content.length}/2000
                    </Form.Text>
                  </div>
                </Form.Group>

                <Row>
                  {/* Target Audience */}
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <FontAwesomeIcon icon={faUsers} className="me-2" />
                        Target Audience <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        value={formData.targetAudience}
                        onChange={(e) => handleChange('targetAudience', e.target.value)}
                      >
                        <option value="all">All Users</option>
                        <option value="speakers">Speakers Only</option>
                        <option value="attendees">Attendees Only</option>
                        <option value="admins">Administrators Only</option>
                      </Form.Select>
                      <Form.Text className="text-muted">
                        {getAudienceDescription(formData.targetAudience)}
                      </Form.Text>
                    </Form.Group>
                  </Col>

                  {/* Priority */}
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <FontAwesomeIcon icon={faExclamationCircle} className="me-2" />
                        Priority Level
                      </Form.Label>
                      <ButtonGroup className="d-flex">
                        <Button
                          variant={formData.priority === 'normal' ? 'primary' : 'outline-primary'}
                          onClick={() => handleChange('priority', 'normal')}
                        >
                          Normal
                        </Button>
                        <Button
                          variant={formData.priority === 'high' ? 'warning' : 'outline-warning'}
                          onClick={() => handleChange('priority', 'high')}
                        >
                          High
                        </Button>
                        <Button
                          variant={formData.priority === 'urgent' ? 'danger' : 'outline-danger'}
                          onClick={() => handleChange('priority', 'urgent')}
                        >
                          Urgent
                        </Button>
                      </ButtonGroup>
                      <Form.Text className="text-muted d-block mt-2">
                        {formData.priority === 'urgent' && 'Urgent announcements are displayed prominently'}
                        {formData.priority === 'high' && 'High priority announcements are highlighted'}
                        {formData.priority === 'normal' && 'Standard announcement display'}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Schedule Type */}
                <Form.Group className="mb-3">
                  <Form.Label>
                    <FontAwesomeIcon icon={faCalendar} className="me-2" />
                    Delivery Schedule
                  </Form.Label>
                  <div className="schedule-options">
                    <Form.Check
                      type="radio"
                      id="schedule-immediate"
                      label="Send Immediately"
                      name="scheduleType"
                      checked={formData.scheduleType === 'immediate'}
                      onChange={() => handleChange('scheduleType', 'immediate')}
                    />
                    <Form.Check
                      type="radio"
                      id="schedule-scheduled"
                      label="Schedule for Later"
                      name="scheduleType"
                      checked={formData.scheduleType === 'scheduled'}
                      onChange={() => handleChange('scheduleType', 'scheduled')}
                    />
                  </div>
                </Form.Group>

                {/* Scheduled Date/Time */}
                {formData.scheduleType === 'scheduled' && (
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Scheduled Date <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="date"
                          value={formData.scheduledDate}
                          onChange={(e) => handleChange('scheduledDate', e.target.value)}
                          isInvalid={!!errors.scheduledDate}
                          min={new Date().toISOString().split('T')[0]}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.scheduledDate}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Scheduled Time <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="time"
                          value={formData.scheduledTime}
                          onChange={(e) => handleChange('scheduledTime', e.target.value)}
                          isInvalid={!!errors.scheduledTime}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.scheduledTime}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                )}

                {/* Preview */}
                {(formData.title || formData.content) && (
                  <div className="announcement-preview mb-3">
                    <h6 className="preview-label">Preview</h6>
                    <Card className="preview-card">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="d-flex align-items-center">
                            <FontAwesomeIcon icon={faBullhorn} className="text-primary me-2" />
                            <strong>{formData.title || 'Announcement Title'}</strong>
                          </div>
                          <Badge bg={
                            formData.priority === 'urgent' ? 'danger' :
                            formData.priority === 'high' ? 'warning' : 'secondary'
                          }>
                            {formData.priority}
                          </Badge>
                        </div>
                        <p className="mb-0 text-muted">
                          {formData.content || 'Announcement content will appear here...'}
                        </p>
                      </Card.Body>
                    </Card>
                  </div>
                )}

                {/* Submit Buttons */}
                <div className="d-flex justify-content-end gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setFormData({
                        title: '',
                        content: '',
                        targetAudience: 'all',
                        priority: 'normal',
                        scheduleType: 'immediate',
                        scheduledDate: '',
                        scheduledTime: ''
                      });
                      setErrors({});
                    }}
                    disabled={sending}
                  >
                    Clear
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={sending || !formData.title.trim() || !formData.content.trim()}
                  >
                    {sending ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        {formData.scheduleType === 'scheduled' ? 'Scheduling...' : 'Sending...'}
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon
                          icon={formData.scheduleType === 'scheduled' ? faCalendar : faPaperPlane}
                          className="me-2"
                        />
                        {formData.scheduleType === 'scheduled' ? 'Schedule Announcement' : 'Send Announcement'}
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Tab>

        <Tab
          eventKey="history"
          title={
            <>
              <FontAwesomeIcon icon={faHistory} className="me-2" />
              History & Analytics
            </>
          }
        >
          <Card>
            <Card.Body>
              {renderHistoryTable()}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {renderDetailsModal()}
    </div>
  );
};

export default AnnouncementCreator;
